import { css } from "styled-components";

/**
 * 내러티브 본문 상자의 기준 치수.
 *
 * 사이트(FeasibilitySite)의 실제 폭에서 나온 값이다. 전역 box-sizing 리셋이
 * 없어 content-box 기준으로 계산된다.
 *
 *   Body  max-width 900,  padding 0 20   -> 900
 *   Card  border 1,       padding 24 28  -> 900 - 2 - 56 = 842  (상자 바깥 폭)
 *   Wrap  border 1,       padding 12 16  -> 842 - 2 - 32 = 808  (본문 폭)
 *
 * 에디터는 관리자 화면 폭만큼 무한정 넓어지므로 같은 치수로 상한을 둔다.
 * 이래야 줄바꿈 위치는 물론 표 열 너비 계산(distributeColumns, Word 임포트)까지
 * 사이트와 맞는다. 사이트 쪽은 이미 이 폭이라 상한이 걸려도 보이는 모습은 그대로다.
 */
export const NARRATIVE_CONTENT_WIDTH = 808;
export const NARRATIVE_PADDING_X = 16;
export const NARRATIVE_PADDING_Y = 12;

// 테두리는 content-box라 바깥에 붙는다. 상자 전체 폭 = 이 값 + 테두리 2px.
export const NARRATIVE_BOX_WIDTH = NARRATIVE_CONTENT_WIDTH + NARRATIVE_PADDING_X * 2;

// 본문 글자 크기. 아래 규칙이 대부분 em이라 이 값이 다르면 제목·첨자·들여쓰기·줄바꿈이
// 전부 어긋난다. 에디터와 조회 화면이 같은 값을 써야 한다.
export const NARRATIVE_FONT_SIZE = 14;

// 목록이 글머리 기호에 내어 주는 왼쪽 자리. Word 임포트가 목록 항목의 들여쓰기를
// 계산할 때 같은 값을 쓴다(중첩 단계마다 이만큼은 이미 들어가 있으므로 빼야 한다).
export const NARRATIVE_LIST_INDENT = 20;

// "1)" 목록의 번호를 세는 카운터 이름. 번호를 이어받는 목록(<ol start="6">)은 이 카운터의
// 시작점도 함께 옮겨야 해서(CSS는 start 속성을 읽지 못한다) 에디터·Word 임포트가 같은 이름을 쓴다.
export const NARRATIVE_LIST_COUNTER = "narrative-paren";

// 셀 기본 테두리. 셀에 인라인 테두리(에디터의 테두리 속성, Word 임포트)를 지정하면
// 그쪽이 이 값을 덮는다. 굵기 상한은 툴바와 Word 임포트가 함께 쓴다.
export const CELL_BORDER_COLOR = "#dee2e6";
export const CELL_BORDER_WIDTH = 1;
export const MAX_CELL_BORDER_WIDTH = 8;

// 표를 가운데·오른쪽에 놓는 여백. 표 위치는 인라인 style로 저장되므로(에디터 표 노드의
// align 속성, Word 임포트) 값을 한 곳에 두어 양쪽이 같은 모양을 읽고 쓰게 한다.
// 왼쪽(기본값)은 아무것도 적지 않는다.
export const TABLE_ALIGN_MARGINS = {
  center: { left: "auto", right: "auto" },
  right: { left: "auto", right: "0" },
};

// 패딩을 자식(.ProseMirror)에게 맡기는 바깥 상자 — 에디터 Wrapper 전용.
// max-width가 패딩 바깥에 걸리므로 상자 전체 폭을 넣는다.
export const narrativeBoxStyles = css`
  max-width: ${NARRATIVE_BOX_WIDTH}px;
`;

// NarrativeEditor가 만들어 낸 HTML을 그리는 규칙. 에디터와 뷰어(SiteNarrativeViewer,
// NarrativeView)가 같은 결과를 보이도록 한 곳에서 관리한다.
//
// 인라인 스타일로 저장되는 것들(글자 크기 span, 들여쓰기 margin-left, 문단·셀 정렬
// text-align, 셀 세로 정렬 vertical-align, 셀 배경색, colgroup 열 너비, tr 행 높이)은
// 여기서 다루지 않는다. 반대로 아래 규칙이 빠지면 저장된 HTML이 같아도 보이는 모습이
// 달라진다.
export const narrativeContentStyles = css`
  line-height: 1.6;

  a { color: #0d6efd; text-decoration: underline; }
  img { max-width: 100%; height: auto; }
  mark { border-radius: 2px; padding: 0 2px; }

  sup, sub { font-size: 0.75em; line-height: 0; }
  sup { vertical-align: super; }
  sub { vertical-align: sub; }

  h1 { font-size: 1.4em; }
  h2 { font-size: 1.2em; }
  h3 { font-size: 1.1em; }

  /* 블록 간격. 브라우저 기본값에 맡기면 화면마다 달라지므로 여기서 정한다.
     앞뒤 여백은 컨테이너 패딩과 겹치지 않게 떨어낸다. */
  p, ul, ol, blockquote, pre, table { margin: 0 0 0.75em; }
  h1, h2, h3, h4, h5, h6 { margin: 1em 0 0.5em; }
  hr { margin: 1em 0; }

  /* 빈 문단은 저장 HTML에서 <p></p>라 높이가 0이 된다. 편집 화면에서는 ProseMirror가
     넣어 주는 <br>이 한 줄을 차지하므로, 뷰어에서도 같은 높이를 갖게 맞춘다.
     (빈 셀이 많은 표에서 행 높이가 눈에 띄게 달라진다) */
  p:empty,
  h1:empty, h2:empty, h3:empty, h4:empty, h5:empty, h6:empty {
    min-height: 1.6em;
  }

  > :first-child { margin-top: 0; }
  > :last-child { margin-bottom: 0; }

  ul, ol { padding-left: ${NARRATIVE_LIST_INDENT}px; }

  /* 브라우저 기본 기호로 낼 수 없는 목록(✓, "1)")은 기호를 직접 그린다.
     list-style-type의 문자열 값("✓ ")도 ::marker의 content도 브라우저마다 지원이
     갈리므로, 어디서나 같게 보이도록 ::before를 기본 기호 자리에 놓는다. */
  ul[data-list-style="check"],
  ol[data-list-style="paren"] {
    list-style: none;

    > li { position: relative; }

    /* 항목 글자의 왼쪽 바깥 — 기본 기호(list-style-position: outside)와 같은 자리 */
    > li::before {
      position: absolute;
      right: 100%;
      margin-right: 0.35em;
      white-space: nowrap;
    }
  }

  ul[data-list-style="check"] > li::before { content: "✓"; }

  /* 번호를 직접 세므로 <ol start="6">처럼 이어 받는 목록은 인라인 counter-reset이 함께
     와야 한다(CSS는 start 속성을 읽지 못한다). 그 인라인 값이 여기 기본값을 덮는다. */
  ol[data-list-style="paren"] {
    counter-reset: ${NARRATIVE_LIST_COUNTER};

    > li { counter-increment: ${NARRATIVE_LIST_COUNTER}; }
    > li::before { content: counter(${NARRATIVE_LIST_COUNTER}) ")"; }
  }

  /* 목록 항목을 가운데·오른쪽으로 정렬하면 글머리 기호(점·번호)도 글자를 따라가야 한다.
     기호는 기본값(list-style-position: outside)이라 항목 바깥 왼쪽에 붙어 있으므로 안쪽으로
     들이고, 문단을 인라인으로 바꿔 기호와 같은 줄에 흐르게 한다(블록으로 두면 기호만 윗줄에
     따로 선다). 인라인이 된 문단은 자기 text-align이 먹지 않으므로 정렬은 항목이 대신 갖는다.
     (왼쪽·양쪽 맞춤은 글자가 왼쪽에서 시작하므로 기본 위치가 맞다) */
  li:has(> p:first-child[style*="text-align: center"]),
  li:has(> p:first-child[style*="text-align: right"]) {
    list-style-position: inside;

    > p:first-child { display: inline; }

    /* 정렬과 기호 위치는 둘 다 아래로 상속된다. 정렬을 따로 지정하지 않은 뒤 문단·하위
       목록까지 끌려가지 않게 되돌린다. (하위 목록에서 정렬한 항목은 자기 규칙으로 다시
       안쪽 기호가 되고, 정렬을 가진 문단은 인라인 style이라 이 규칙보다 세다) */
    > :not(:first-child) {
      text-align: left;
      list-style-position: outside;
    }
  }

  li:has(> p:first-child[style*="text-align: center"]) { text-align: center; }
  li:has(> p:first-child[style*="text-align: right"]) { text-align: right; }

  /* 인라인이 된 문단은 아래 여백을 잃는다. 그 문단이 항목의 전부일 때만 항목 쪽에
     돌려주어야 정렬하지 않은 항목과 줄 간격이 같아진다. */
  li:has(> p:first-child:last-child[style*="text-align: center"]),
  li:has(> p:first-child:last-child[style*="text-align: right"]) {
    margin-bottom: 0.75em;
  }

  /* TaskItem 구조: li > label(체크박스) + div(본문 문단) */
  ul[data-type="taskList"] {
    list-style: none;
    padding-left: 0;

    li {
      display: flex;
      align-items: flex-start;
      gap: 8px;

      /* 체크박스도 글자를 따라간다. 항목이 flex라 text-align이 먹지 않으므로
         체크박스와 본문을 묶어 줄 전체를 옮긴다. */
      &:has(> div > p:first-child[style*="text-align: center"]) { justify-content: center; }
      &:has(> div > p:first-child[style*="text-align: right"]) { justify-content: flex-end; }

      /* 본문이 남은 폭을 다 차지하면 옮길 자리가 생기지 않는다 */
      &:has(> div > p:first-child[style*="text-align: center"]) > div,
      &:has(> div > p:first-child[style*="text-align: right"]) > div {
        flex: 0 1 auto;
      }

      /* 체크박스를 본문 첫 줄 높이에 맞춰 세로 중앙 정렬 */
      > label {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        height: 1.6em;
        user-select: none;
      }

      input[type="checkbox"] {
        margin: 0;
      }

      /* 문단 기본 여백이 남아 있으면 글자가 체크박스보다 아래로 밀린다 */
      > div {
        flex: 1 1 auto;
        min-width: 0;

        > p:first-child { margin-top: 0; }
        > p:last-child { margin-bottom: 0; }
      }
    }
  }

  /* 표를 감싸는 가로 스크롤 상자. 편집 중에는 tiptap이, 뷰어에서는 withTableWrappers가
     붙여 준다(저장 HTML에는 래퍼가 없다). 이게 없으면 본문 폭보다 넓은 표 — 특히 셀 안에
     들어 있는 표 — 가 상자를 뚫고 나가 뒤 내용 위에 겹쳐 그려진다.
     표의 바깥 여백은 래퍼가 대신 갖는다. */
  .tableWrapper {
    overflow-x: auto;
    margin: 0 0 0.75em;
    padding-bottom: 2px;

    > table { margin: 0; }
  }

  table {
    /* fixed 레이아웃이라야 셀 내용 길이에 따라 열 너비가 흔들리지 않는다.
       열 너비가 지정된 표는 tiptap이 넣은 인라인 width가 이 100%를 덮어쓴다. */
    table-layout: fixed;
    border-collapse: collapse;
    width: 100%;

    /* 열 너비를 지정하지 않은 표가 본문 폭을 넘지 않게 막는다. 열 너비가 px로 지정된
       표에는 걸리지 않는다(fixed 레이아웃은 열 너비의 합을 max-width보다 우선한다).
       그래서 열 너비 자체를 본문 폭 안에 맞춰 저장하는 쪽은 에디터가 맡는다. */
    max-width: 100%;

    td, th {
      border: ${CELL_BORDER_WIDTH}px solid ${CELL_BORDER_COLOR};
      padding: 6px 10px;
      overflow-wrap: break-word;

      /* 셀에 지정한 vertical-align/text-align은 인라인 스타일이라 이 기본값을 덮는다 */
      vertical-align: top;

      /* 셀 안 문단은 위아래 여백을 떨어내야 셀 높이가 화면마다 달라지지 않는다 */
      > :first-child { margin-top: 0; }
      > :last-child { margin-bottom: 0; }
    }

    /* 인라인 background-color가 있으면 그쪽이 이긴다. :not([style])로 거르면
       정렬만 지정한 머리글도 기본 배경을 잃는다. */
    th {
      background: #f8f9fa;
      font-weight: 600;

      /* HTML 표준 기본 스타일시트가 th를 가운데 정렬하므로 직접 되돌린다.
         이게 없으면 툴바가 "좌"라고 표시하는데도 머리글만 가운데로 보인다. */
      text-align: left;
    }
  }
`;

// 저장된 내러티브를 그대로 보여 주는 상자(사이트 뷰어, 버전 내용, 변경 내역)가
// 공통으로 쓰는 기하와 스타일. 테두리·배경만 각자 얹으면 된다.
//
// 에디터 Wrapper와 달리 여기서는 패딩이 상자 자신에게 붙는다. content-box라
// max-width가 패딩 안쪽을 가리키므로, BOX_WIDTH가 아니라 본문 폭을 넣어야
// 에디터와 같은 808px가 된다. (BOX_WIDTH를 넣으면 본문이 32px 넓어져
// 줄바꿈 위치와 표 열 너비가 에디터에서 보던 것과 달라진다)
export const narrativeViewerStyles = css`
  padding: ${NARRATIVE_PADDING_Y}px ${NARRATIVE_PADDING_X}px;
  max-width: ${NARRATIVE_CONTENT_WIDTH}px;
  font-size: ${NARRATIVE_FONT_SIZE}px;

  /* 넓은 표가 들어와도 바깥 화면이 가로로 밀리지 않게 한다 */
  overflow-x: auto;

  ${narrativeContentStyles}

  /* 조회 전용이므로 체크박스를 누를 수 없다 */
  input[type="checkbox"] { pointer-events: none; }
`;
